import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import { Button, Text, Panel, Switch, H3, H4, Fieldset, FormGroup, Radio, Input, Dropdown, Checkbox, Small, Box } from '@bigcommerce/big-design';
import { AddIcon, ArrowDropDownIcon } from '@bigcommerce/big-design-icons';

import { updateSettings } from '../../redux/actions/settings';
import { locationsIndex } from '../../redux/actions/locations';
import { cartLayoutsIndex } from '../../redux/actions/cartLayouts';

import PageHeader from '../partials/PageHeader';

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import FeatureCard from '../partials/FeatureCard';

import UploadStoreLogo from './UploadStoreLogo';

const Settings = ({ payload, settings, submitSettings, locations, cartLayouts, loadLocations, loadCartLayouts }) => {
  const [submitted, setSubmitted] = useState(false);

  const [pageSize, setPageSize] = useState(settings.settings['page_size']);
  const [shippingOrigin, setShippingOrigin] = useState(null);
  const [defaultLayout, setDefaultLayout] = useState(null);
  const [defaultPickType, setDefaultPickType] = useState(settings.settings['default_pick_type']);
  const [maxOrdersPerBatch, setMaxOrdersPerBatch] = useState(settings.settings['max_orders_per_batch']);
  const [maxItemsPerBatch, setMaxItemsPerBatch] = useState(settings.settings['max_items_per_batch']);
  const [maxItemsPerTote, setMaxItemsPerTote] = useState(settings.settings['max_items_per_tote']);
  const [defaultCartLayoutId, setDefaultCartLayoutId] = useState(null);

  useEffect(() => {
    loadLocations(payload);
    loadCartLayouts(payload);
  }, []);

  useEffect(() => {
    if(!locations.isFetching && locations.status === 'success') {
      setShippingOrigin(locations.locations.find(location => location.id === settings.settings['shipping_origin_location']));
    }
  }, [locations]);

  useEffect(() => {
    if(!cartLayouts.isFetching && cartLayouts.status === 'success') {
      setDefaultLayout(cartLayouts.cartLayouts.find(layout => layout.id === settings.settings['default_cart_layout']));
    }
  }, [cartLayouts]);


  useEffect(() => {
    if(!settings.isFetching && settings.status === 'success') {
      setSubmitted(false);
    };
  }, [settings])

  const setSize = (event) => {
    let size = event.target.value;
    setPageSize(size);
  }

  const setPickingType = (event) => {
    let type = event.target.value;
    setDefaultPickType(type);
  }

  const addressItems = () => {
    let items = [];

    locations.locations.map(location => 
      items.push(
        {
         content: location.label,
         onItemClick: () => setShippingOrigin(location),
         hash: location.id 
        }
      )
    );

    return items;
  }

  const cartLayoutItems = () => {
    let items = [];

    cartLayouts.cartLayouts.map(layout =>
      items.push(
        {
          content: layout.label,
          onItemClick: () => setDefaultLayout(layout),
          hash: layout.id
        }
      )
    )

    return items;
  };

  const newSettingsJson = () => {
    return {
      page_size: pageSize,
      logo_location: settings.settings.logo_location,
      shipping_origin_location: shippingOrigin?.id,
      default_pick_type: defaultPickType,
      max_orders_per_batch: maxOrdersPerBatch,
      max_items_per_batch: maxItemsPerBatch,
      max_items_per_tote: maxItemsPerTote,
      default_cart_layout: defaultLayout?.id
    }
  }

  const submit = () => {
    setSubmitted(true);
    submitSettings(payload, newSettingsJson());
  }

  const description = () => {
    return(
      <> 
        <Text>Upload a logo by choosing a file and clicking the "Upload Logo" button. This is the logo that is used at the top left of your PDFs for purchase orders, shipment lists and warehouse receiving orders.</Text>
        <Text>Manage your payment settings and view invoices by clicking the "View Stripe Customer Portal" link. A link will be emailed to you once you enter your email address.</Text>
        <Text>Select the appropriate page size from either the US Letter or A4 page size radio buttons.</Text>
        <Text>When you're done updating your settings, click the "Save Page Settings" button to update how purchase order PDFs will be generated.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#943737' helpLink="" />
      <SettingsPage title="App Settings" description={description()}>
        <UploadStoreLogo 
          payload={payload}
        />

        <div className="flex-container">
          <div className="flex-1 padding-15">
            <FeatureCard
              title="Cart Layouts"
              description="Manage batch picking carts. Define tote layouts during order batching for any picking type."
              buttonText="Cart Layouts"
              image={<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 20 20"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 5.5a2.489 2.489 0 0 0-.5089-1.512c1.0017-1.0012 2.252-1.7536 3.6505-2.1566C7.9842 2.8034 8.9108 3.5 10 3.5s2.0157-.6966 2.3584-1.6686c1.3984.403 2.6488 1.1554 3.6505 2.1566A2.4885 2.4885 0 0 0 15.5 5.5C15.5 6.8807 16.6193 8 18 8c.0878 0 .1746-.0046.2601-.0134A8.5197 8.5197 0 0 1 18.5 10c0 .6936-.0831 1.3679-.2399 2.0133A2.5403 2.5403 0 0 0 18 12c-1.3807 0-2.5 1.1193-2.5 2.5 0 .5682.1895 1.0921.5089 1.5119-1.0017 1.0012-2.2521 1.7536-3.6505 2.1566C12.0157 17.1965 11.0892 16.5 10 16.5s-2.0157.6965-2.3584 1.6685c-1.3984-.403-2.6488-1.1554-3.6505-2.1566A2.4887 2.4887 0 0 0 4.5 14.5C4.5 13.1193 3.3807 12 2 12c-.0878 0-.1746.0045-.2601.0133A8.5204 8.5204 0 0 1 1.5 10c0-.6937.083-1.368.2399-2.0134A2.529 2.529 0 0 0 2 8c1.3807 0 2.5-1.1193 2.5-2.5zm5.5 7c1.3807 0 2.5-1.1193 2.5-2.5S11.3807 7.5 10 7.5 7.5 8.6193 7.5 10s1.1193 2.5 2.5 2.5z"/></svg>}
              linkUrl="/cart_layouts"
              stripeBackground='#943737'
            />
          </div>  
          <div className="flex-1 padding-15">
            <FeatureCard
              title="Warehouse Zones"
              description="Manage zones in your warehouse for zone picking. Assign products to zones to batch orders into picking totes correctly."
              buttonText="Warehouse Zones"
              image={<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 20 20"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 5.5a2.489 2.489 0 0 0-.5089-1.512c1.0017-1.0012 2.252-1.7536 3.6505-2.1566C7.9842 2.8034 8.9108 3.5 10 3.5s2.0157-.6966 2.3584-1.6686c1.3984.403 2.6488 1.1554 3.6505 2.1566A2.4885 2.4885 0 0 0 15.5 5.5C15.5 6.8807 16.6193 8 18 8c.0878 0 .1746-.0046.2601-.0134A8.5197 8.5197 0 0 1 18.5 10c0 .6936-.0831 1.3679-.2399 2.0133A2.5403 2.5403 0 0 0 18 12c-1.3807 0-2.5 1.1193-2.5 2.5 0 .5682.1895 1.0921.5089 1.5119-1.0017 1.0012-2.2521 1.7536-3.6505 2.1566C12.0157 17.1965 11.0892 16.5 10 16.5s-2.0157.6965-2.3584 1.6685c-1.3984-.403-2.6488-1.1554-3.6505-2.1566A2.4887 2.4887 0 0 0 4.5 14.5C4.5 13.1193 3.3807 12 2 12c-.0878 0-.1746.0045-.2601.0133A8.5204 8.5204 0 0 1 1.5 10c0-.6937.083-1.368.2399-2.0134A2.529 2.529 0 0 0 2 8c1.3807 0 2.5-1.1193 2.5-2.5zm5.5 7c1.3807 0 2.5-1.1193 2.5-2.5S11.3807 7.5 10 7.5 7.5 8.6193 7.5 10s1.1193 2.5 2.5 2.5z"/></svg>}
              linkUrl="/zones"
              stripeBackground='#943737'
            />
          </div>
        </div>

        <Panel>
          <H3>Settings</H3>

          <div className="flex-container">
            <div className="flex-1 padding-15">
              <H4>PDF Page Size</H4>
              <FormGroup>
                <Radio checked={pageSize === "LETTER"} value="LETTER" label="US Letter" onChange={(e) => setSize(e)} />
                <Radio checked={pageSize === "A4"} value="A4" label="A4" onChange={(e) => setSize(e)} />
              </FormGroup>   

            </div>
          
            <div className="flex-1 padding-15">
              <H4>Shipping Origin</H4>
              { locations.status !== 'success' &&
                <LoadingSpinner size="xxSmall" statusText="Loading locations"/>
              }

              { locations.status === 'success' &&
                <>
                  <Dropdown
                    items={addressItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{shippingOrigin?.label || "Select Address"}</Button>}
                  />
                </>
              }
            </div>

          </div>

          <Input
            label="Max Orders per Cart Batch"
            placeholder="Max Orders per Cart Batch"
            id="maxOrdersPerBatch"
            type="number"
            min="1"
            value={ maxOrdersPerBatch }
            onChange={ (e) => setMaxOrdersPerBatch(e.target.value) }
          />


          <Input
            label="Max Items per Cart Batch"
            placeholder="Max Items per Cart Batch"
            id="maxItemsPerBatch"
            type="number"
            min="1"
            value={ maxItemsPerBatch }
            onChange={ (e) => setMaxItemsPerBatch(e.target.value) }
          />

          <Input
            label="Max Items per Tote"
            placeholder="Max Items per Tote"
            id="maxItemsPerTote"
            type="number"
            min="1"
            value={ maxItemsPerTote }
            onChange={ (e) => setMaxItemsPerTote(e.target.value) }
          />

          <H4>Picking Type</H4>
          <FormGroup>
            <Radio checked={defaultPickType === "order"} value="order" label="Batch Order" onChange={(e) => setPickingType(e)} />
            <Radio checked={defaultPickType === "zone_sequential"} value="zone_sequential" label="Zone Sequential" onChange={(e) => setPickingType(e)} />
            <Radio checked={defaultPickType === "zone_concurrent"} value="zone_concurrent" label="Zone Concurrent" onChange={(e) => setPickingType(e)} />
          </FormGroup>

          <H4>Default Cart Style</H4>
          { cartLayouts.status !== 'success' &&
            <LoadingSpinner size="xxSmall" statusText="Loading cart layouts"/>
          }

          { cartLayouts.status === 'success' &&
            <>
              <Dropdown
                items={cartLayoutItems()}
                maxHeight={250}
                placement="bottom-start"
                toggle={<Button iconRight={<ArrowDropDownIcon />}>{defaultLayout?.label || "Select Cart Layout"}</Button>}
              />
            </>
          }

          <br/>

          <Button
            disabled={submitted}
            onClick={() => submit()}
          >
            Save Settings
          </Button>

        </Panel>
      </SettingsPage>
    </>
  )
}

const mapStateToProps = state => ({
  stores: state.stores,
  settings: state.settings,
  locations: state.locations,
  cartLayouts: state.cartLayouts,
});

const mapDispatchToProps = (dispatch) => ({
  submitSettings: (payload, newSettings) => dispatch(updateSettings(payload, newSettings)),
  loadLocations: (payload) => dispatch(locationsIndex(payload)),
  loadCartLayouts: (payload) => dispatch(cartLayoutsIndex(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);